import { ArrowPathIcon } from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'

const HOLD_DURATION_MS = 2300

const HoldToConfirmButton = ({
	id,
	label,
	loadingLabel,
	holdLabel,
	helperText,
	holdingHelperText,
	busy,
	disabled,
	onConfirm,
	className,
	progressClassName,
	progressTrackClassName,
	icon: Icon,
	wrapperClassName
}) => {
	const [isHolding, setIsHolding] = useState(false)
	const [progress, setProgress] = useState(0)
	const [hasConfirmed, setHasConfirmed] = useState(false)
	const frameRef = useRef(null)
	const holdStartedAtRef = useRef(0)
	const hasConfirmedRef = useRef(false)

	const reset = () => {
		if (frameRef.current) {
			window.cancelAnimationFrame(frameRef.current)
			frameRef.current = null
		}

		setIsHolding(false)
		setProgress(0)
		setHasConfirmed(false)
		hasConfirmedRef.current = false
	}

	useEffect(() => () => reset(), [])

	useEffect(() => {
		if (!busy && hasConfirmed) {
			reset()
		}
	}, [busy, hasConfirmed])

	const completeHold = () => {
		if (frameRef.current) {
			window.cancelAnimationFrame(frameRef.current)
			frameRef.current = null
		}

		setProgress(1)
		setIsHolding(false)
		setHasConfirmed(true)
		hasConfirmedRef.current = true
		onConfirm()
	}

	const tick = now => {
		const elapsed = now - holdStartedAtRef.current
		const nextProgress = Math.min(elapsed / HOLD_DURATION_MS, 1)

		setProgress(nextProgress)

		if (nextProgress >= 1) {
			completeHold()
			return
		}

		frameRef.current = window.requestAnimationFrame(tick)
	}

	const startHold = () => {
		if (disabled || busy || isHolding || hasConfirmed || hasConfirmedRef.current) return

		holdStartedAtRef.current = window.performance.now()
		setIsHolding(true)
		setProgress(0)
		frameRef.current = window.requestAnimationFrame(tick)
	}

	const cancelHold = () => {
		if (busy || hasConfirmedRef.current) return

		if (frameRef.current) {
			window.cancelAnimationFrame(frameRef.current)
			frameRef.current = null
		}

		setIsHolding(false)
		setProgress(0)
	}

	const handlePointerDown = event => {
		if (event.pointerType === 'mouse' && event.button !== 0) return

		event.preventDefault()
		startHold()
	}

	const handleKeyDown = event => {
		if (event.repeat) return
		if (event.key !== ' ' && event.key !== 'Enter') return

		event.preventDefault()
		startHold()
	}

	const handleKeyUp = event => {
		if (event.key !== ' ' && event.key !== 'Enter') return

		event.preventDefault()
		cancelHold()
	}

	const activeLabel = busy ? loadingLabel : isHolding ? holdLabel : label
	const activeHelperText = busy ? 'Processing request...' : isHolding ? holdingHelperText : helperText
	const helperId = id ? `${id}-helper` : undefined
	const progressValue = busy || hasConfirmed ? 1 : progress
	const circumference = 2 * Math.PI * 8
	const progressDasharray = `${progressValue * circumference} ${circumference}`
	const shouldFocusProgress = isHolding || busy || hasConfirmed
	const shouldShowInitialIcon = !shouldFocusProgress
	const shouldShowTick = hasConfirmed && !busy

	return (
		<div className={`w-full sm:w-auto ${wrapperClassName}`}>
			<button
				id={id}
				type="button"
				className={`relative isolate inline-flex w-full items-center justify-center overflow-hidden rounded font-semibold shadow-sm transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-60 ${className}`}
				onClick={event => event.preventDefault()}
				onPointerDown={handlePointerDown}
				onPointerUp={cancelHold}
				onPointerLeave={cancelHold}
				onPointerCancel={cancelHold}
				onTouchEnd={cancelHold}
				onTouchCancel={cancelHold}
				onKeyDown={handleKeyDown}
				onKeyUp={handleKeyUp}
				disabled={disabled || busy}
				aria-busy={busy}
				aria-disabled={disabled || busy}
				aria-describedby={helperId}
			>
				<span
					className={`absolute inset-0 origin-left ${progressClassName}`}
					style={{ transform: `scaleX(${progress})` }}
					aria-hidden="true"
				/>
				<span className="relative z-10 flex w-full items-center gap-2.5 px-3 py-2 text-sm">
					<span className="relative flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/20">
						<span
							className={`absolute inset-[2px] rounded-full bg-black/15 transition-transform duration-300 ease-out ${
								shouldFocusProgress ? 'scale-0' : 'scale-100'
							}`}
							aria-hidden="true"
						/>
						{Icon && (
							<Icon
								className={`absolute h-3.5 w-3.5 text-white transition-all duration-200 ${
									shouldShowInitialIcon
										? 'translate-y-0 scale-100 opacity-100'
										: '-translate-y-1 scale-75 opacity-0'
								}`}
								aria-hidden="true"
							/>
						)}
						<svg
							className={`absolute inset-0 h-full w-full fill-none transition-transform duration-300 ease-out ${
								shouldFocusProgress ? 'scale-100' : 'scale-90'
							}`}
							viewBox="0 0 32 32"
							aria-hidden="true"
						>
							<circle
								cx="16"
								cy="16"
								r="8"
								fill="none"
								stroke="currentColor"
								strokeWidth="16"
								className={progressTrackClassName}
							/>
							<circle
								cx="16"
								cy="16"
								r="8"
								fill="none"
								stroke="currentColor"
								strokeWidth="16"
								strokeDasharray={progressDasharray}
								className="origin-center -rotate-90 text-white transition-[stroke-dasharray] ease-linear"
								style={{ transitionDuration: `${HOLD_DURATION_MS}ms`, transformOrigin: '50% 50%' }}
							/>
						</svg>
						{shouldShowTick && (
							<svg
								className="absolute inset-0 h-full w-full fill-none"
								viewBox="0 0 24 24"
								aria-hidden="true"
							>
								<polyline
									points="18,7 11,16 6,12"
									className="stroke-white transition-[stroke-dashoffset] duration-300 ease-out"
									style={{
										strokeWidth: 3,
										strokeLinecap: 'round',
										strokeLinejoin: 'round',
										strokeDasharray: 18,
										strokeDashoffset: shouldShowTick ? 0 : 18
									}}
								/>
							</svg>
						)}
						{busy && (
							<ArrowPathIcon className="absolute h-3.5 w-3.5 animate-spin text-white" aria-hidden="true" />
						)}
					</span>
					<span className="min-w-0 flex-1 text-left leading-tight">{activeLabel}</span>
				</span>
			</button>
			<p id={helperId} className="mt-1 text-center text-[11px] leading-4 text-gray-500">
				{activeHelperText}
			</p>
		</div>
	)
}

HoldToConfirmButton.propTypes = {
	id: PropTypes.string,
	label: PropTypes.string.isRequired,
	loadingLabel: PropTypes.string.isRequired,
	holdLabel: PropTypes.string.isRequired,
	helperText: PropTypes.string.isRequired,
	holdingHelperText: PropTypes.string.isRequired,
	busy: PropTypes.bool.isRequired,
	disabled: PropTypes.bool,
	onConfirm: PropTypes.func.isRequired,
	className: PropTypes.string.isRequired,
	progressClassName: PropTypes.string.isRequired,
	progressTrackClassName: PropTypes.string,
	icon: PropTypes.elementType.isRequired,
	wrapperClassName: PropTypes.string
}

HoldToConfirmButton.defaultProps = {
	id: undefined,
	disabled: false,
	progressTrackClassName: 'text-white/25',
	wrapperClassName: ''
}

export default HoldToConfirmButton
